<template>
  <div class="step-relations">
    <div class="step-relations__head">
      <p class="step-relations__intro">{{ copy.StepIntro }}</p>

      <div v-if="hasRoot" class="step-relations__views" role="tablist" :aria-label="copy.ViewsLabel">
        <button
          type="button"
          role="tab"
          class="step-relations__view"
          :class="{ 'step-relations__view--on': view === RelationsView.Tree }"
          :aria-selected="String(view === RelationsView.Tree)"
          @click="setView(RelationsView.Tree)">
          <v-icon x-small>mdi-file-tree-outline</v-icon>
          {{ copy.TreeTab }}
        </button>
        <button
          type="button"
          role="tab"
          class="step-relations__view"
          :class="{ 'step-relations__view--on': view === RelationsView.Diagram }"
          :aria-selected="String(view === RelationsView.Diagram)"
          @click="setView(RelationsView.Diagram)">
          <v-icon x-small>mdi-graph-outline</v-icon>
          {{ copy.DiagramTab }}
        </button>
      </div>
    </div>

    <!-- The grain warning names the relation responsible, which is why grain() returns its id. -->
    <div v-if="fanOutNode" class="step-relations__grain">
      <v-icon small class="step-relations__grain-icon">mdi-alert-outline</v-icon>
      <div>
        <span class="step-relations__grain-title">{{ copy.FanOutBannerTitle }} — {{ fanOutNode.label }}</span>
        <span class="step-relations__grain-body">{{ copy.FanOutBannerBody }}</span>
      </div>
    </div>

    <div v-if="!hasRoot" class="ds-empty-state">
      <v-icon class="ds-empty-state__icon">mdi-source-branch</v-icon>
      <div class="ds-empty-state__title">{{ copy.NoRootTitle }}</div>
      <div class="ds-empty-state__description step-relations__muted">{{ copy.NoRootMessage }}</div>
    </div>

    <v-alert
      v-else-if="!graph"
      :type="graphLoading ? 'info' : 'error'"
      outlined
      dense
      :role="graphLoading ? 'status' : 'alert'">
      <div class="d-flex align-center justify-space-between">
        <span>
          {{ graphLoading
            ? 'Loading the relations available from this starting data…'
            : 'The relations for this starting data could not be loaded.' }}
        </span>
        <v-btn
          v-if="!graphLoading"
          text
          small
          color="error"
          @click="$emit('retry-graph')">
          Try again
        </v-btn>
      </div>
    </v-alert>

    <relation-tree
      v-if="hasRoot && view === RelationsView.Tree"
      :nodes="viewNodes"
      :active-node-id="activeNodeId"
      @add="openAdd"
      @configure="openConfigure"
      @remove="removeRelation" />

    <relation-diagram
      v-else-if="hasRoot"
      :nodes="viewNodes"
      :active-node-id="activeNodeId"
      @add="openAdd"
      @configure="openConfigure"
      @remove="removeRelation"
      @close-popover="closePopover" />

    <edge-popover
      v-if="popover"
      ref="popover"
      class="step-relations__popover"
      :style="popover.style"
      :graph="graph"
      :parent-node-id="popover.parentNodeId"
      :relation-state="model.state"
      :candidate-edge="popoverCandidateEdge"
      :parent-kind="popover.parentKind"
      :parent-label="popover.parentLabel"
      :existing-edge="popover.edge"
      :register-options="registerOptions"
      :register-options-loading="registerOptionsLoading"
      @submit="applyPopover"
      @cancel="closePopover" />
  </div>
</template>

<script>
  import { log, error } from '@/utils/debugLogger';
  import { RootNodeId, isScalarStrategy } from '../../model/definitionVocabulary';
  import RelationTree from '../relations/RelationTree.vue';
  import RelationDiagram from '../relations/RelationDiagram.vue';
  import EdgePopover from '../relations/EdgePopover.vue';
  import { RelationsCopy, RelationsView, describeStrategyChip, humanizeWireWord } from '../relations/relationsCopy';
  import {
    GraphCardinality,
    canAddMoreEdges,
    cardinalityOf,
    findGraphEdge,
    graphEdgesFrom,
  } from '../relations/relationsGraph';

  /** How the popover is placed against the control that opened it, in the step's own box. */
  const PopoverWidth = 360;
  const PopoverMargin = 8;
  const PopoverGap = 6;

  const NodeIcon = Object.freeze({
    Root: 'mdi-database-outline',
    Scalar: 'mdi-table-column',
    Relation: 'mdi-link-variant',
  });

  /**
   * Step 2 of the composite source wizard: which linked data the source carries.
   *
   * Two views of ONE definition — a nested tree and a node diagram — so the step owns everything
   * they share: the view nodes both render, the popover both open, and every call into the model.
   * The views themselves are drawings; they report what the author clicked and the id of the node
   * it happened on, and nothing else.
   *
   * The step reads the definition through `nodes()`, `grain()` and `state.edges`, and writes it
   * through `addEdge`, `updateEdgeStrategy`, `updateEdgeDiscriminator` and `removeEdge`. It holds
   * node IDs, never edge objects: every edge mutator replaces the object it changes, so a
   * reference kept across a mutation is stale by the time it is read.
   *
   * What it adds on top of the model are the three rules that live in the authoring graph, which
   * the model never sees: the server's `maxEdges` cap, a traversal that demands a tag kind, and
   * `fanOut` relations forming one ancestor-to-descendant chain.
   */
  export default {
    name: 'StepRelations',

    components: { RelationTree, RelationDiagram, EdgePopover },

    props: {
      /** The definition model for this authoring session — the only thing this step mutates. */
      model: { type: Object, required: true },
      /** The authoring graph for the definition, or null while it has none. */
      graph: { type: Object, default: null },
      /** True while the local root graph is being resolved. */
      graphLoading: { type: Boolean, default: false },
      registerOptions: { type: Array, default: () => [] },
      registerOptionsLoading: { type: Boolean, default: false },
      /** The workspace target. Part of the shared step seam; the relations step only logs it. */
      target: { type: Object, default: null },
      /**
       * True while the source is published: browsing and focus stay, every mutation affordance
       * goes. Expressed through the view-node flags so the tree and diagram stay dumb drawings.
       */
      readonly: { type: Boolean, default: false },
    },

    data() {
      return {
        copy: RelationsCopy,
        RelationsView,
        view: RelationsView.Tree,
        popover: null,
      };
    },

    computed: {
      /** Nodes as the model describes them, dressed with what the two views need to draw them. */
      viewNodes() {
        const nodes = this.model.nodes();
        const edges = this.model.state.edges;
        const grainNodeId = this.model.grain();
        const canAddMore = canAddMoreEdges(this.graph, edges.length);

        return nodes.map((node) => {
          const isRoot = node.id === RootNodeId;
          const edge = isRoot ? null : edges.find((candidate) => candidate.id === node.id);
          const strategy = edge ? edge.strategy : null;
          const graphEdge = edge
            ? findGraphEdge(
              this.graph,
              this.parentKindOf(nodes, node),
              edge.target,
              edge.traversalKey,
            )
            : null;

          return {
            id: node.id,
            parentId: node.parentId,
            label: node.label,
            kind: node.type,
            isRoot,
            isScalar: node.isScalar,
            icon: isRoot ? NodeIcon.Root : (node.isScalar ? NodeIcon.Scalar : NodeIcon.Relation),
            detail: this.detailOf(node, isRoot, graphEdge),
            strategyKind: strategy ? strategy.kind : null,
            strategyChip: describeStrategyChip(strategy),
            shiftsGrain: !isRoot && node.id === grainNodeId,
            canAdd: !this.readonly && !node.isScalar && canAddMore
              && graphEdgesFrom(this.graph, node.type).length > 0,
            canConfigure: !this.readonly && !isRoot && !!graphEdge,
            canRemove: !this.readonly && !isRoot,
            // While published, the wizard's read-only notice is the one explanation; a per-node
            // note about why nothing can be added would just restate it.
            addNote: isRoot && !this.readonly ? this.rootAddNote(node, canAddMore) : null,
          };
        });
      },

      hasRoot() {
        return this.viewNodes.length > 0;
      },

      /** The relation that made a row mean something else, so the banner can name it. */
      fanOutNode() {
        const grainNodeId = this.model.grain();
        if (grainNodeId === RootNodeId) return null;
        return this.viewNodes.find((node) => node.id === grainNodeId) || null;
      },

      /** The node the open popover belongs to, so its card reads as the one being worked on. */
      activeNodeId() {
        if (!this.popover) return null;
        return this.popover.edge ? this.popover.edge.id : this.popover.parentNodeId;
      },

      /** Candidate identity and parent used to evaluate fan-out without mutating the model. */
      popoverCandidateEdge() {
        if (!this.popover) return {};
        return {
          id: this.popover.edge ? this.popover.edge.id : null,
          fromNodeId: this.popover.parentNodeId,
        };
      },
    },

    beforeDestroy() {
      this.unwatchOutsideClicks();
    },

    methods: {
      /** Node kind of a node's parent — what the graph keys its traversals by. */
      parentKindOf(nodes, node) {
        const parent = nodes.find((candidate) => candidate.id === node.parentId);
        return parent ? parent.type : null;
      },

      /**
       * The card's second line: the physical table the node reads from, which is the closest thing
       * the payload carries to "how this is joined", plus the cardinality when the graph reports it.
       */
      detailOf(node, isRoot, graphEdge) {
        if (isRoot) return (this.graph && this.graph.rootTable) || humanizeWireWord(node.type);

        const table = (graphEdge && graphEdge.targetTable) || humanizeWireWord(node.type);
        const cardinality = cardinalityOf(graphEdge);
        if (!cardinality) return table;

        const word = cardinality === GraphCardinality.ToMany ? this.copy.ToManyBadge : this.copy.ToOneBadge;
        return `${table} · ${word}`;
      },

      /** Why the root offers no "Add relation", or null when it does. */
      rootAddNote(node, canAddMore) {
        if (this.graphLoading) return this.copy.GraphLoadingNote;
        if (!this.graph) return this.copy.NoGraphNote;
        if (!canAddMore) return this.copy.EdgeLimitNote;
        if (!graphEdgesFrom(this.graph, node.type).length) return this.copy.NoTraversalsNote;
        return null;
      },

      setView(view) {
        if (this.view === view) return;
        log('[StepRelations] view changed', { view });
        this.closePopover();
        this.view = view;
      },

      openAdd({ nodeId, rect }) {
        const node = this.viewNodes.find((candidate) => candidate.id === nodeId);
        if (!node) return;

        this.showPopover({
          parentNodeId: node.id,
          parentKind: node.kind,
          parentLabel: node.label,
          edge: null,
        }, rect);
      },

      /**
       * Reconfiguring an existing relation. The popover is anchored to the relation, but works
       * against ITS PARENT's traversals — that is the pair the graph keys an edge by.
       */
      openConfigure({ nodeId, rect }) {
        const node = this.viewNodes.find((candidate) => candidate.id === nodeId);
        const edge = this.model.state.edges.find((candidate) => candidate.id === nodeId);
        if (!node || !edge) return;

        const parent = this.viewNodes.find((candidate) => candidate.id === node.parentId);
        this.showPopover({
          parentNodeId: edge.fromNodeId,
          parentKind: parent ? parent.kind : null,
          parentLabel: parent ? parent.label : '',
          edge,
        }, rect);
      },

      /**
       * Places the panel relative to the control that opened it, in the step's own coordinates,
       * and clamped/sized so it is always fully visible inside the step and never clipped by
       * the footer or header.
       */
      showPopover(context, rect) {
        const host = this.$el.getBoundingClientRect();
        const anchor = rect || host;

        const PopoverDefaultMaxHeight = 460;
        const hostWidth = host.width || 600;
        const hostHeight = host.height || 500;

        // Horizontal positioning: align with anchor left, clamped inside host bounds
        const anchorLeft = anchor.left - host.left;
        const left = Math.max(
          PopoverMargin,
          Math.min(anchorLeft, hostWidth - PopoverWidth - PopoverMargin),
        );

        // Vertical positioning:
        const anchorTop = anchor.top - host.top;
        const anchorBottom = anchor.bottom - host.top;
        const spaceBelow = hostHeight - anchorBottom - PopoverGap - PopoverMargin;
        const spaceAbove = anchorTop - PopoverGap - PopoverMargin;

        let top;
        let maxHeight;

        if (spaceBelow >= 300 || spaceBelow >= spaceAbove) {
          // Open below anchor
          top = Math.max(PopoverMargin, anchorBottom + PopoverGap);
          const desiredHeight = Math.min(PopoverDefaultMaxHeight, hostHeight - 2 * PopoverMargin);
          if (top + desiredHeight > hostHeight - PopoverMargin) {
            top = Math.max(PopoverMargin, hostHeight - desiredHeight - PopoverMargin);
          }
          maxHeight = Math.max(200, hostHeight - top - PopoverMargin);
        } else {
          // Open above anchor
          const desiredHeight = Math.min(PopoverDefaultMaxHeight, spaceAbove);
          top = Math.max(PopoverMargin, anchorTop - desiredHeight - PopoverGap);
          maxHeight = Math.max(200, anchorTop - top - PopoverGap);
        }

        this.popover = {
          ...context,
          style: {
            left: `${Math.round(left)}px`,
            top: `${Math.round(top)}px`,
            maxHeight: `${Math.round(maxHeight)}px`,
          },
        };
        this.watchOutsideClicks();
      },

      closePopover() {
        if (!this.popover) return;
        log('[StepRelations] relation panel closed');
        this.popover = null;
        this.unwatchOutsideClicks();
      },

      /** Adds the relation, or re-decides an existing one — whichever the panel was opened for. */
      applyPopover(payload) {
        const context = this.popover;
        if (!context) return;
        const previousTargetId = (context.edge && context.edge.targetId) || null;

        try {
          if (context.edge) this.reconfigureEdge(context.edge, payload);
          else this.addEdge(context.parentNodeId, payload);
          const nextTargetId = payload.targetId || null;
          if (nextTargetId !== previousTargetId) this.$emit('bridge-target-changed', nextTargetId);
        } catch (err) {
          error('[StepRelations] the definition refused this relation', { payload, err });
          this.$dialog.notify.error(err.message, { position: 'top-right', timeout: 5000 });
        }

        this.closePopover();
      },

      addEdge(fromNodeId, payload) {
        const edgeId = this.model.addEdge({
          fromNodeId,
          traversalKey: payload.traversalKey || undefined,
          traversalLabel: payload.traversalLabel || undefined,
          target: payload.target,
          targetId: payload.targetId || undefined,
          discriminator: payload.discriminator,
          strategy: payload.strategy,
          alias: payload.alias || undefined,
        });

        log('[StepRelations] relation added', { edgeId, from: fromNodeId, target: payload.target });
      },

      /**
       * Both mutators are non-destructive and each owns one decision, so only the ones that
       * actually changed are called — re-applying an unchanged strategy is safe but says something
       * happened that did not.
       */
      reconfigureEdge(edge, payload) {
        const edgeId = edge.id;

        if ((payload.traversalKey || null) !== (edge.traversalKey || null)) {
          this.model.updateEdgeTraversal(
            edgeId,
            payload.traversalKey,
            payload.traversalLabel,
          );
        }

        if ((payload.targetId || null) !== (edge.targetId || null)) {
          this.model.updateEdgeTargetId(edgeId, payload.targetId);
        }

        if ((payload.discriminator || null) !== (edge.discriminator || null)) {
          this.model.updateEdgeDiscriminator(edgeId, payload.discriminator);
        }

        // `column` belongs in this comparison for the same reason `orderBy` does: it is a key the
        // strategy owns, so a `list` relation whose aggregated property changed HAS changed, and
        // leaving it out discarded that edit without saying anything.
        const strategyChanged = payload.strategy.kind !== edge.strategy.kind
          || (payload.strategy.orderBy || null) !== (edge.strategy.orderBy || null)
          || (payload.strategy.column || null) !== (edge.strategy.column || null);

        if (strategyChanged) this.model.updateEdgeStrategy(edgeId, payload.strategy);

        // The alias is applied AFTER any strategy change, because re-strategizing mints its own
        // and would otherwise overwrite what the author just typed.
        const aliasChanged = !!payload.alias && payload.alias !== edge.alias;
        if (aliasChanged) this.model.setEdgeAlias(edgeId, payload.alias);

        log('[StepRelations] relation reconfigured', {
          edgeId,
          strategyChanged,
          aliasChanged,
          strategy: payload.strategy.kind,
          scalar: isScalarStrategy(payload.strategy.kind),
        });
      },

      /** Removing cascades in the model: child relations, their columns, and the tag source. */
      removeRelation(edgeId) {
        const edge = this.model.state.edges.find((candidate) => candidate.id === edgeId);
        try {
          this.model.removeEdge(edgeId);
          if (edge && edge.target === 'registerRows') {
            this.$emit('bridge-target-changed', null);
          }
          log('[StepRelations] relation removed', { edgeId });
        } catch (err) {
          error('[StepRelations] failed to remove the relation', { edgeId, err });
          this.$dialog.notify.error(err.message, { position: 'top-right', timeout: 5000 });
        }

        // The cascade can take the node the panel was opened on with it.
        const stillThere = this.viewNodes.some((node) => node.id === this.activeNodeId);
        if (this.popover && !stillThere) this.closePopover();
      },

      watchOutsideClicks() {
        if (this.outsideClickHandler) return;
        this.outsideClickHandler = (event) => {
          const panel = this.$refs.popover;
          if (panel && panel.$el && !panel.$el.contains(event.target)) this.closePopover();
        };
        // Next tick, so the click that opened the panel is not the click that closes it.
        this.$nextTick(() => document.addEventListener('mousedown', this.outsideClickHandler));
      },

      unwatchOutsideClicks() {
        if (!this.outsideClickHandler) return;
        document.removeEventListener('mousedown', this.outsideClickHandler);
        this.outsideClickHandler = null;
      },
    },
  };
</script>

<style scoped>
  /*
   * One palette for the step and both views. Custom properties inherit, so the tree, the diagram
   * and the popover read these from here rather than each carrying its own copy of the colours.
   *
   * The muted tone is spelled out because the design system's own subtitle colour resolves to
   * --v-text_secondary-base (#C8C8C8), a light grey meant for dark surfaces — unreadable on the
   * white the wizard renders on.
   */
  .step-relations {
    position: relative;
    min-height: 480px;
    height: 100%;
    display: flex;
    flex-direction: column;
    --rel-surface: #fcfdfc;
    --rel-surface-sunken: #f6f8f7;
    --rel-border: #dfe3e1;
    --rel-border-strong: #c7cdcb;
    --rel-ink: #182420;
    --rel-ink-muted: #5c6b66;
    --rel-primary: var(--v-primary-base, #03624c);
    --rel-primary-soft: rgba(3, 98, 76, 0.08);
    --rel-warn-ink: #8a5600;
    --rel-warn-line: #c77d00;
    --rel-canvas-dot: rgba(3, 98, 76, 0.07);
  }

  .theme--dark .step-relations {
    --rel-surface: #232322;
    --rel-surface-sunken: #1c1d1b;
    --rel-border: #3a3d3b;
    --rel-border-strong: #4d514f;
    --rel-ink: #ffffff;
    --rel-ink-muted: var(--v-text_secondary-base);
    --rel-warn-ink: #e0a33c;
    --rel-canvas-dot: rgba(44, 194, 149, 0.12);
  }

  .step-relations__head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
  }

  .step-relations__intro {
    flex: 1;
    margin: 0;
    font-size: 12.5px;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .step-relations__views {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 9px;
    background: var(--rel-surface-sunken, #f6f8f7);
  }

  .step-relations__view {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 13px;
    border-radius: 7px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--rel-ink-muted, #5c6b66);
  }

  .step-relations__view--on {
    background: var(--rel-surface, #fcfdfc);
    color: var(--rel-primary, #03624c);
    box-shadow: 0 1px 3px rgba(2, 26, 26, 0.12);
  }

  .step-relations__grain {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-bottom: 14px;
    padding: 10px 13px;
    border: 1px solid var(--rel-warn-line, #c77d00);
    border-radius: 8px;
    font-size: 12.5px;
    color: var(--rel-warn-ink, #8a5600);
  }

  .step-relations__grain-icon {
    color: var(--rel-warn-ink, #8a5600);
  }

  .step-relations__grain-title {
    display: block;
    font-weight: 600;
  }

  .step-relations__grain-body {
    display: block;
  }

  .step-relations__popover {
    position: absolute;
    z-index: 20;
  }

  .step-relations__muted {
    color: var(--rel-ink-muted, #5c6b66);
  }
</style>
